/**
 * Error Navigation Utility
 *
 * Maps form field paths to form steps and provides utilities
 * to navigate users to the step containing the first validation error.
 */
import { FieldErrors } from "react-hook-form";

import { createStepErrorNavigation } from "src/cases/common/stepErrorNavigation";
import { ALL_STEPS, StepId } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";

/**
 * Maps field path prefixes to their corresponding step IDs.
 * Order matters - more specific paths should come before generic ones.
 */
const FIELD_PATH_TO_STEP_MAP: Array<{ pathPrefix: string; stepId: StepId }> = [
  { pathPrefix: "attributes.version_name", stepId: "define-version-settings" },
  {
    pathPrefix: "attributes.case_properties",
    stepId: "define-case-properties",
  },
  {
    pathPrefix: "attributes.case_blocks_configs",
    stepId: "configure-case-blocks",
  },
  {
    pathPrefix: "attributes.file_categories",
    stepId: "configure-file-categories",
  },
  { pathPrefix: "attributes.case_tasks", stepId: "define-tasks" },
  { pathPrefix: "attributes.case_actions", stepId: "configure-case-actions" },
  { pathPrefix: "attributes.copilot_settings", stepId: "configure-copilot" },
  { pathPrefix: "attributes.workflow_rules", stepId: "define-workflow-rules" },
  {
    pathPrefix: "attributes.case_creation_flow_details",
    stepId: "select-decision-flow",
  },
];

const navigation = createStepErrorNavigation(FIELD_PATH_TO_STEP_MAP);

export const getStepIdForFieldPath = navigation.getStepIdForFieldPath;

export const getStepsWithErrors = (
  errors: FieldErrors<CaseTemplateFormValues>,
): StepId[] => navigation.getStepsWithErrors(errors);

export const stepHasErrors = (
  errors: FieldErrors<CaseTemplateFormValues>,
  stepId: StepId,
): boolean => navigation.stepHasErrors(errors, stepId);

export const getFirstErrorStep = (
  errors: FieldErrors<CaseTemplateFormValues>,
): StepId | undefined => navigation.getFirstErrorStep(errors, ALL_STEPS);
